import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { usePractice } from '../../context/PracticeContext';
import Tooltip from '../../components/Tooltip';

function CampaignTypes() {
  const { selectedPracticeId } = usePractice();
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [eligibilityTypes, setEligibilityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    description: '',
    eligibility_type: 'patient_inactive',
    eligibility_config: { months_threshold: 12 },
    // GH-65: surface template selection on the Create dialog so admins
    // don't have to discover the separate Cadence Editor. Empty values
    // trigger the backend's auto-pick of the first available templates.
    sms_template_id: '',
    email_template_id: '',
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Cadence modal state
  const [showCadenceModal, setShowCadenceModal] = useState(false);
  const [selectedCampaignType, setSelectedCampaignType] = useState(null);
  const [cadences, setCadences] = useState([]);
  const [cadenceLoading, setCadenceLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showCadenceForm, setShowCadenceForm] = useState(false);
  const [editingCadence, setEditingCadence] = useState(null);
  const [cadenceFormData, setCadenceFormData] = useState({
    days_after_enrollment: 0,
    sms_template_id: '',
    email_template_id: ''
  });

  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Voice settings modal state
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceSaving, setVoiceSaving] = useState(false);

  // Rule builder state
  const [ruleBuilderSchema, setRuleBuilderSchema] = useState(null);

  useEffect(() => {
    loadEligibilityTypes();
    loadRuleBuilderSchema();
  }, []);

  const loadRuleBuilderSchema = async () => {
    try {
      const response = await api.getRuleBuilderSchema();
      setRuleBuilderSchema(response.data);
    } catch (err) {
      console.error('Failed to load rule builder schema:', err);
    }
  };

  useEffect(() => {
    if (selectedPracticeId) {
      loadCampaignTypes();
      // GH-65: pre-load templates so the Create dialog can render the SMS
      // and Email template dropdowns without waiting for the user to open
      // the separate cadence editor first.
      api.getTemplates(selectedPracticeId)
        .then(res => setTemplates(res.data || []))
        .catch(err => console.warn('Failed to pre-load templates:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPracticeId]);

  const loadEligibilityTypes = async () => {
    try {
      const response = await api.getEligibilityTypes();
      setEligibilityTypes(response.data || []);
    } catch (err) {
      console.error('Failed to load eligibility types:', err);
    }
  };

  const loadCampaignTypes = async () => {
    try {
      setLoading(true);
      const [typesRes, configsRes] = await Promise.all([
        api.getCampaignTypes(selectedPracticeId, true),
        api.getCampaignConfigs(selectedPracticeId)
      ]);
      setCampaignTypes(typesRes.data || []);
      setConfigs(configsRes.data || []);
    } catch (err) {
      setError('Failed to load campaign settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleConfig = async (campaignType, field, currentValue) => {
    try {
      await api.updateCampaignConfig(campaignType, {
        [field]: currentValue ? 0 : 1,
      }, selectedPracticeId);
      // Reload configs
      const configsRes = await api.getCampaignConfigs(selectedPracticeId);
      setConfigs(configsRes.data || []);
    } catch (err) {
      alert(`Error updating config: ${err.message}`);
    }
  };

  const handleInitialize = async () => {
    if (!selectedPracticeId) return;

    try {
      await api.initializeCampaignTypes(selectedPracticeId);
      loadCampaignTypes();
    } catch (err) {
      alert(err.message || 'Failed to initialize campaign types');
    }
  };

  const handleSubmit = async (e, runAfterCreate = false) => {
    e.preventDefault();
    setFormError('');

    if (!formData.slug.trim() || !formData.name.trim()) {
      setFormError('Slug and name are required');
      return;
    }

    setSaving(true);
    try {
      let dataToSend;

      if (editingType) {
        // For system campaign types, only send allowed fields
        if (editingType.is_system) {
          dataToSend = {
            name: formData.name,
            description: formData.description,
            eligibility_config: formData.eligibility_config
          };
        } else {
          // For custom types, send all fields
          dataToSend = {
            ...formData,
            practice_id: selectedPracticeId
          };
        }
        await api.updateCampaignType(editingType.id, dataToSend);
      } else {
        // Creating new - send all fields
        dataToSend = {
          ...formData,
          practice_id: selectedPracticeId
        };
        const createRes = await api.createCampaignType(dataToSend);

        // Run immediately if requested
        if (runAfterCreate && createRes.data?.id) {
          const runRes = await api.runCampaignType(createRes.data.id);
          alert(`Campaign created and run!\nEnrolled: ${runRes.data?.enrolled || 0}\nContacted: ${runRes.data?.contacted || 0}`);
        }
      }

      setShowForm(false);
      setEditingType(null);
      resetForm();
      loadCampaignTypes();
    } catch (err) {
      setFormError(err.message || 'Failed to save campaign type');
    } finally {
      setSaving(false);
    }
  };

  // Reset form to clean state — used after save/cancel
  const resetForm = () => {
    setFormData({
      slug: '',
      name: '',
      description: '',
      eligibility_type: 'patient_inactive',
      eligibility_config: { months_threshold: 12 },
      sms_template_id: '',
      email_template_id: '',
    });
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      slug: type.slug,
      name: type.name,
      description: type.description || '',
      eligibility_type: type.eligibility_type,
      eligibility_config: typeof type.eligibility_config === 'string'
        ? JSON.parse(type.eligibility_config)
        : type.eligibility_config,
      // GH-203: pre-populate the template dropdowns from the row's current
      // ids or the selectors always land back on "Auto-pick" and every save
      // wiped the saved value (frontend sent '' which the model wrote as NULL).
      sms_template_id: type.sms_template_id ? String(type.sms_template_id) : '',
      email_template_id: type.email_template_id ? String(type.email_template_id) : '',
    });
    setShowForm(true);
    setFormError('');
  };

  const handleDelete = async (type) => {
    if (type.is_system) {
      alert('Cannot delete system campaign types');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${type.name}"?`)) {
      return;
    }

    try {
      await api.deleteCampaignType(type.id);
      loadCampaignTypes();
    } catch (err) {
      alert(err.message || 'Failed to delete campaign type');
    }
  };

  const handleActivate = async (type) => {
    try {
      await api.activateCampaignType(type.id);
      loadCampaignTypes();
    } catch (err) {
      alert(err.message || 'Failed to activate campaign type');
    }
  };

  const handleDeactivate = async (type) => {
    try {
      await api.deactivateCampaignType(type.id);
      loadCampaignTypes();
    } catch (err) {
      alert(err.message || 'Failed to deactivate campaign type');
    }
  };

  const handleEligibilityTypeChange = (eligibilityType) => {
    const typeConfig = eligibilityTypes.find(t => t.type === eligibilityType);
    const defaultConfig = {};

    if (eligibilityType === 'custom_rules') {
      // Initialize with default rule builder config
      defaultConfig.target = 'patients';
      defaultConfig.rules = [];
      defaultConfig.logic = 'AND';
    } else if (typeConfig?.config_schema) {
      Object.entries(typeConfig.config_schema).forEach(([key, schema]) => {
        defaultConfig[key] = schema.default || '';
      });
    }

    setFormData({
      ...formData,
      eligibility_type: eligibilityType,
      eligibility_config: defaultConfig
    });
  };

  const handleConfigChange = (key, value) => {
    setFormData({
      ...formData,
      eligibility_config: {
        ...formData.eligibility_config,
        [key]: value
      }
    });
  };

  // Rule Builder Functions
  const addRule = () => {
    const newRule = {
      field: '',
      operator: '',
      value: ''
    };
    setFormData({
      ...formData,
      eligibility_config: {
        ...formData.eligibility_config,
        rules: [...(formData.eligibility_config.rules || []), newRule]
      }
    });
  };

  const updateRule = (index, key, value) => {
    const rules = [...(formData.eligibility_config.rules || [])];
    rules[index] = { ...rules[index], [key]: value };

    // Reset operator and value when field changes
    if (key === 'field') {
      rules[index].operator = '';
      rules[index].value = '';
    }

    setFormData({
      ...formData,
      eligibility_config: {
        ...formData.eligibility_config,
        rules
      }
    });
  };

  const removeRule = (index) => {
    const rules = (formData.eligibility_config.rules || []).filter((_, i) => i !== index);
    setFormData({
      ...formData,
      eligibility_config: {
        ...formData.eligibility_config,
        rules
      }
    });
  };

  const getFieldType = (fieldName, target) => {
    if (!ruleBuilderSchema) return 'string';
    const field = ruleBuilderSchema.fields[target]?.find(f => f.value === fieldName);
    return field?.type || 'string';
  };

  const getOperatorsForField = (fieldName, target) => {
    if (!ruleBuilderSchema) return [];
    const fieldType = getFieldType(fieldName, target);
    return ruleBuilderSchema.operators[fieldType] || [];
  };

  const getFieldOptions = (fieldName, target) => {
    if (!ruleBuilderSchema) return [];
    const field = ruleBuilderSchema.fields[target]?.find(f => f.value === fieldName);
    return field?.options || [];
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingType(null);
    resetForm();
    setFormError('');
  };

  // Cadence management
  const openCadenceModal = async (type) => {
    setSelectedCampaignType(type);
    setShowCadenceModal(true);
    setCadenceLoading(true);
    setShowCadenceForm(false);
    setEditingCadence(null);

    try {
      const [cadencesRes, templatesRes] = await Promise.all([
        api.getCampaignCadences(type.id),
        api.getTemplates(selectedPracticeId)
      ]);
      setCadences(cadencesRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (err) {
      console.error('Failed to load cadences:', err);
    } finally {
      setCadenceLoading(false);
    }
  };

  const resetCadenceForm = () => {
    setCadenceFormData({
      days_after_enrollment: 0,
      sms_template_id: '',
      email_template_id: ''
    });
    setEditingCadence(null);
    setShowCadenceForm(false);
  };

  const handleEditCadence = (cadence) => {
    setEditingCadence(cadence);
    setCadenceFormData({
      days_after_enrollment: cadence.days_after_enrollment,
      sms_template_id: cadence.sms_template_id || '',
      email_template_id: cadence.email_template_id || ''
    });
    setShowCadenceForm(true);
  };

  const handleSaveCadence = async () => {
    if (!selectedCampaignType) return;

    try {
      const dataToSend = {
        days_after_enrollment: parseInt(cadenceFormData.days_after_enrollment, 10),
        sms_template_id: cadenceFormData.sms_template_id || null,
        email_template_id: cadenceFormData.email_template_id || null
      };

      if (editingCadence) {
        await api.updateCampaignCadence(selectedCampaignType.id, editingCadence.id, dataToSend);
      } else {
        await api.addCampaignCadence(selectedCampaignType.id, dataToSend);
      }

      const response = await api.getCampaignCadences(selectedCampaignType.id);
      setCadences(response.data || []);
      resetCadenceForm();
    } catch (err) {
      alert(err.message || 'Failed to save cadence step');
    }
  };

  const handleDeleteCadence = async (cadenceId) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;

    try {
      await api.deleteCampaignCadence(selectedCampaignType.id, cadenceId);
      const response = await api.getCampaignCadences(selectedCampaignType.id);
      setCadences(response.data || []);
    } catch (err) {
      alert(err.message || 'Failed to delete cadence step');
    }
  };

  const smsTemplates = templates.filter(t => t.channel === 'sms');
  const emailTemplates = templates.filter(t => t.channel === 'email');

  // Preview eligibility
  const handlePreview = async (type) => {
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewData(null);

    try {
      const response = await api.previewCampaignTypeEligibility(type.id, selectedPracticeId);
      setPreviewData(response.data);
    } catch (err) {
      console.error('Failed to load preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Run campaign
  const handleRunCampaign = async (type) => {
    if (!window.confirm(`Run the "${type.name}" campaign now?`)) return;

    try {
      const result = await api.runCampaignType(type.id);
      alert(`Campaign completed!\nEnrolled: ${result.data.enrolled}\nContacted: ${result.data.contacted}`);
    } catch (err) {
      alert(err.message || 'Failed to run campaign');
    }
  };

  // Voice settings
  const openVoiceModal = async (type) => {
    setSelectedCampaignType(type);
    setShowVoiceModal(true);
    setVoiceLoading(true);

    try {
      const response = await api.getCampaignConfigs(selectedPracticeId);
      const config = response.data?.find(c => c.campaign_type === type.slug) || {};
      setVoiceConfig({
        voice_enabled: config.voice_enabled || 0,
        voice_channel_priority: config.voice_channel_priority || 2,
        voice_max_attempts: config.voice_max_attempts || 2,
        voice_quiet_hours_start: config.voice_quiet_hours_start || '20:00',
        voice_quiet_hours_end: config.voice_quiet_hours_end || '09:00',
        voice_retry_delay_hours: config.voice_retry_delay_hours || 48,
        sms_fallback_enabled: config.sms_fallback_enabled !== undefined ? config.sms_fallback_enabled : 1
      });
    } catch (err) {
      console.error('Failed to load voice config:', err);
      setVoiceConfig({
        voice_enabled: 0,
        voice_channel_priority: 2,
        voice_max_attempts: 2,
        voice_quiet_hours_start: '20:00',
        voice_quiet_hours_end: '09:00',
        voice_retry_delay_hours: 48,
        sms_fallback_enabled: 1
      });
    } finally {
      setVoiceLoading(false);
    }
  };

  const handleSaveVoiceConfig = async () => {
    if (!selectedCampaignType || !voiceConfig) return;

    try {
      setVoiceSaving(true);
      await api.updateCampaignConfig(selectedCampaignType.slug, voiceConfig, selectedPracticeId);
      setShowVoiceModal(false);
      alert('Voice settings saved successfully!');
    } catch (err) {
      alert(err.message || 'Failed to save voice settings');
    } finally {
      setVoiceSaving(false);
    }
  };

  const renderConfigInput = (key, schema, value) => {
    if (schema.type === 'select') {
      return (
        <select
          value={value || schema.default}
          onChange={(e) => handleConfigChange(key, e.target.value)}
          style={styles.input}
        >
          {schema.options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (schema.type === 'number') {
      return (
        <input
          type="number"
          value={value || schema.default}
          onChange={(e) => handleConfigChange(key, parseInt(e.target.value, 10))}
          min={schema.min}
          max={schema.max}
          style={styles.input}
        />
      );
    }

    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => handleConfigChange(key, e.target.value)}
        placeholder={schema.placeholder}
        style={styles.input}
      />
    );
  };

  if (!selectedPracticeId) {
    return (
      <div>
        <div style={styles.subNav}>
          <Link to="/practice-view/campaigns" style={styles.subNavTab}>
            Campaigns
          </Link>
          <Link to="/practice-view/campaign-types" style={{ ...styles.subNavTab, ...styles.subNavTabActive }}>
            Campaign Settings
          </Link>
        </div>
        <div className="card">
          <h2>Campaign Types</h2>
          <p>Please select a practice from the header to manage campaign types.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <div style={styles.subNav}>
          <Link to="/practice-view/campaigns" style={styles.subNavTab}>
            Campaigns
          </Link>
          <Link to="/practice-view/campaign-types" style={{ ...styles.subNavTab, ...styles.subNavTabActive }}>
            Campaign Settings
          </Link>
        </div>
        <div className="loading">Loading campaign types...</div>
      </div>
    );
  }

  const currentEligibilityType = eligibilityTypes.find(t => t.type === formData.eligibility_type);

  // Merge campaign types with their configs
  const getConfigForType = (slug) => configs.find(c => c.campaign_type === slug) || {};

  return (
    <div>
      {/* Sub-navigation tabs */}
      <div style={styles.subNav}>
        <Link to="/practice-view/campaigns" style={styles.subNavTab}>
          Campaigns
        </Link>
        <Link to="/practice-view/campaign-types" style={{ ...styles.subNavTab, ...styles.subNavTabActive }}>
          Campaign Settings
        </Link>
      </div>

      <div className="card">
        <div style={styles.header}>
          <h2>Campaign Settings</h2>
          <div style={styles.headerActions}>
            {campaignTypes.length === 0 && (
              <button className="button secondary" onClick={handleInitialize}>
                Initialize System Types
              </button>
            )}
            {!showForm && (
              <button
                className="button"
                onClick={() => {
                  setShowForm(true);
                  setEditingType(null);
                  resetForm();
                }}
              >
                + Add Campaign Type
              </button>
            )}
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {showForm && (
          <div style={styles.formContainer}>
            <h3>{editingType ? 'Edit Campaign Type' : 'New Campaign Type'}</h3>
            {formError && <div className="error">{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Slug * <Tooltip text="Unique identifier for this campaign type (lowercase, hyphens only)" /></label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    style={styles.input}
                    placeholder="e.g., birthday-reminder"
                    disabled={saving || (editingType && editingType.is_system)}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name * <Tooltip text="Display name shown to users for this campaign type" /></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={styles.input}
                    placeholder="Birthday Reminder Campaign"
                    disabled={saving}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description <Tooltip text="Brief explanation of this campaign's purpose and target audience" /></label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={styles.textarea}
                  placeholder="Description of this campaign type"
                  rows={2}
                  disabled={saving}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Eligibility Type * <Tooltip text="Criteria used to determine which patients qualify for this campaign" /></label>
                <select
                  value={formData.eligibility_type}
                  onChange={(e) => handleEligibilityTypeChange(e.target.value)}
                  style={styles.input}
                  disabled={saving || (editingType && editingType.is_system)}
                >
                  {eligibilityTypes.map(type => (
                    <option key={type.type} value={type.type}>
                      {type.name} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {currentEligibilityType?.config_schema && formData.eligibility_type !== 'custom_rules' && (
                <div style={styles.configSection}>
                  <label style={styles.label}>Eligibility Configuration</label>
                  <div style={styles.configGrid}>
                    {Object.entries(currentEligibilityType.config_schema).map(([key, schema]) => (
                      <div key={key} style={styles.configItem}>
                        <label style={styles.configLabel}>{schema.label}</label>
                        {renderConfigInput(key, schema, formData.eligibility_config[key])}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rule Builder for custom_rules */}
              {formData.eligibility_type === 'custom_rules' && ruleBuilderSchema && (
                <div style={styles.configSection}>
                  <label style={styles.label}>Rule Builder <Tooltip text="Define custom eligibility rules by combining field conditions" /></label>

                  <div style={styles.ruleBuilderHeader}>
                    <div style={styles.formGroup}>
                      <label style={styles.configLabel}>Target <Tooltip text="The data source to evaluate rules against (e.g., patients table)" /></label>
                      <select
                        value={formData.eligibility_config.target || 'patients'}
                        onChange={(e) => handleConfigChange('target', e.target.value)}
                        style={styles.input}
                      >
                        {ruleBuilderSchema.targets.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.configLabel}>Match Logic <Tooltip text="AND: all rules must match. OR: any single rule match qualifies" /></label>
                      <select
                        value={formData.eligibility_config.logic || 'AND'}
                        onChange={(e) => handleConfigChange('logic', e.target.value)}
                        style={styles.input}
                      >
                        <option value="AND">All rules must match (AND)</option>
                        <option value="OR">Any rule must match (OR)</option>
                      </select>
                    </div>
                  </div>

                  <div style={styles.rulesContainer}>
                    <label style={styles.configLabel}>Rules <Tooltip text="Individual conditions that filter eligible patients" /></label>
                    {(formData.eligibility_config.rules || []).map((rule, index) => (
                      <div key={index} style={styles.ruleRow}>
                        <select
                          value={rule.field}
                          onChange={(e) => updateRule(index, 'field', e.target.value)}
                          style={{ ...styles.input, flex: 1 }}
                        >
                          <option value="">Select Field</option>
                          {ruleBuilderSchema.fields[formData.eligibility_config.target || 'patients']?.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>

                        <select
                          value={rule.operator}
                          onChange={(e) => updateRule(index, 'operator', e.target.value)}
                          style={{ ...styles.input, flex: 1 }}
                          disabled={!rule.field}
                        >
                          <option value="">Select Operator</option>
                          {getOperatorsForField(rule.field, formData.eligibility_config.target || 'patients').map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>

                        {/* Value input - changes based on operator */}
                        {rule.operator && !['is_null', 'is_not_null'].includes(rule.operator) && (
                          getFieldType(rule.field, formData.eligibility_config.target || 'patients') === 'select' ? (
                            <select
                              value={rule.value}
                              onChange={(e) => updateRule(index, 'value', e.target.value)}
                              style={{ ...styles.input, flex: 1 }}
                            >
                              <option value="">Select Value</option>
                              {getFieldOptions(rule.field, formData.eligibility_config.target || 'patients').map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={getFieldType(rule.field, formData.eligibility_config.target || 'patients') === 'number' ? 'number' : 'text'}
                              value={rule.value || ''}
                              onChange={(e) => updateRule(index, 'value', e.target.value)}
                              style={{ ...styles.input, flex: 1 }}
                              placeholder={rule.operator?.includes('months') ? 'Number of months' : 'Value'}
                            />
                          )
                        )}

                        <button
                          type="button"
                          onClick={() => removeRule(index)}
                          style={{ ...styles.actionButton, ...styles.dangerButton }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="button secondary"
                      onClick={addRule}
                      style={{ marginTop: '8px' }}
                    >
                      + Add Rule
                    </button>
                  </div>
                </div>
              )}

              {/* GH-65: template selection surfaced on BOTH Create and Edit
                  now. Original Create-only gate confused reporters who
                  expected to change the day-0 template without opening
                  Configure Cadence. Per-step templates still live in the
                  cadence editor; this selector just pins the day-0 default. */}
              {(
                <div style={styles.configSection}>
                  <label style={styles.label}>Message Templates <Tooltip text="Picked for the day-0 cadence step. Leave blank to auto-pick the practice's first active templates. Per-step overrides live in Configure Cadence." /></label>
                  <div style={styles.configGrid}>
                    <div style={styles.configItem}>
                      <label style={styles.configLabel}>SMS Template</label>
                      <select
                        style={styles.input}
                        value={formData.sms_template_id}
                        onChange={(e) => setFormData({ ...formData, sms_template_id: e.target.value })}
                      >
                        <option value="">-- Auto-pick first SMS template --</option>
                        {smsTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.template_name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.configItem}>
                      <label style={styles.configLabel}>Email Template</label>
                      <select
                        style={styles.input}
                        value={formData.email_template_id}
                        onChange={(e) => setFormData({ ...formData, email_template_id: e.target.value })}
                      >
                        <option value="">-- Auto-pick first email template --</option>
                        {emailTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.template_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {smsTemplates.length === 0 && emailTemplates.length === 0 && (
                    <div style={{ fontSize: 12, color: '#6c757d', marginTop: 6 }}>
                      No templates available — campaign will fall back to generic hardcoded messages. Create templates in the Templates section first to send branded messages.
                    </div>
                  )}
                </div>
              )}

              <div style={styles.formActions}>
                <button type="submit" className="button" disabled={saving}>
                  {saving ? 'Saving...' : editingType ? 'Update' : 'Create'}
                </button>
                {!editingType && (
                  <button
                    type="button"
                    className="button"
                    style={{ background: '#28a745' }}
                    disabled={saving}
                    onClick={(e) => handleSubmit(e, true)}
                  >
                    {saving ? 'Running...' : 'Create & Run Now'}
                  </button>
                )}
                <button
                  type="button"
                  className="button secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Eligibility <Tooltip text="Rule that determines which patients qualify for this campaign" /></th>
              <th>Type <Tooltip text="System: built-in campaign type. Custom: user-created" /></th>
              <th style={{ textAlign: 'center' }}>Enabled <Tooltip text="Toggle to activate or deactivate this campaign for new enrollments" /></th>
              <th style={{ textAlign: 'center' }}>SMS <Tooltip text="Whether SMS messages are sent for this campaign" /></th>
              <th style={{ textAlign: 'center' }}>Email <Tooltip text="Whether email messages are sent for this campaign" /></th>
              <th>Status <Tooltip text="Active: campaign is operational. Inactive: campaign is disabled" /></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaignTypes.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: '#666' }}>
                  No campaign types found. Click "Initialize System Types" to create the default types.
                </td>
              </tr>
            ) : (
              campaignTypes.map((type) => {
                const config = getConfigForType(type.slug);
                return (
                  <tr key={type.id}>
                    <td>
                      <strong>{type.name}</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>{type.slug}</div>
                    </td>
                    <td>{type.eligibility_type}</td>
                    <td>
                      <span className={`badge ${type.is_system ? 'info' : 'secondary'}`}>
                        {type.is_system ? 'System' : 'Custom'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={config.enabled === 1}
                        onChange={() => handleToggleConfig(type.slug, 'enabled', config.enabled)}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={config.sms_enabled === 1}
                        onChange={() => handleToggleConfig(type.slug, 'sms_enabled', config.sms_enabled)}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={config.email_enabled === 1}
                        onChange={() => handleToggleConfig(type.slug, 'email_enabled', config.email_enabled)}
                      />
                    </td>
                    <td>
                      <span className={`badge ${type.is_active ? 'success' : 'danger'}`}>
                        {type.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={styles.actions}>
                        <button
                          style={styles.actionButton}
                          onClick={() => handleEdit(type)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          style={styles.actionButton}
                          onClick={() => openCadenceModal(type)}
                          title="Configure message cadence"
                        >
                          Cadence
                        </button>
                        <button
                          style={styles.actionButton}
                          onClick={() => openVoiceModal(type)}
                          title="Configure voice AI settings"
                        >
                          Voice
                        </button>
                        <button
                          style={styles.actionButton}
                          onClick={() => handlePreview(type)}
                          title="Preview eligible patients"
                        >
                          Preview
                        </button>
                        <button
                          style={{ ...styles.actionButton, ...styles.successButton }}
                          onClick={() => handleRunCampaign(type)}
                          title="Run campaign now"
                        >
                          Run
                        </button>
                        {type.is_active ? (
                          <button
                            style={{ ...styles.actionButton, ...styles.warningButton }}
                            onClick={() => handleDeactivate(type)}
                            title="Deactivate"
                          >
                            Disable
                          </button>
                        ) : (
                          <button
                            style={{ ...styles.actionButton, ...styles.successButton }}
                            onClick={() => handleActivate(type)}
                            title="Activate"
                          >
                            Enable
                          </button>
                        )}
                        {!type.is_system && (
                          <button
                            style={{ ...styles.actionButton, ...styles.dangerButton }}
                            onClick={() => handleDelete(type)}
                            title="Delete"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Cadence Modal */}
      {showCadenceModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Message Cadence - {selectedCampaignType?.name}</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Configure the sequence of messages sent after enrollment.
            </p>

            {cadenceLoading ? (
              <p>Loading...</p>
            ) : (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Step <Tooltip text="Sequential order in which messages are sent" /></th>
                      <th>Days After Enrollment <Tooltip text="Number of days after patient enrollment when this message is sent" /></th>
                      <th>SMS Template <Tooltip text="Text message template used for this cadence step" /></th>
                      <th>Email Template <Tooltip text="Email template used for this cadence step" /></th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cadences.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', color: '#666' }}>
                          No cadence steps configured.
                        </td>
                      </tr>
                    ) : (
                      cadences.map((cadence) => (
                        <tr key={cadence.id}>
                          <td>{cadence.step_number + 1}</td>
                          <td>Day {cadence.days_after_enrollment}</td>
                          <td>{cadence.sms_template_name || 'Not set'}</td>
                          <td>{cadence.email_template_name || 'Not set'}</td>
                          <td>
                            <div style={styles.actions}>
                              <button
                                style={styles.actionButton}
                                onClick={() => handleEditCadence(cadence)}
                              >
                                Edit
                              </button>
                              <button
                                style={{
                                  ...styles.actionButton,
                                  ...styles.dangerButton,
                                  ...(cadences.length <= 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                                }}
                                onClick={() => handleDeleteCadence(cadence.id)}
                                disabled={cadences.length <= 1}
                                title={cadences.length <= 1 ? 'At least one cadence step is required' : 'Delete this step'}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Cadence Form */}
                {showCadenceForm ? (
                  <div style={styles.cadenceForm}>
                    <h4>{editingCadence ? 'Edit Step' : 'Add New Step'}</h4>
                    <div style={styles.cadenceFormRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Days After Enrollment * <Tooltip text="Day 0 = enrollment day. Set the delay before this message is sent" /></label>
                        <input
                          type="number"
                          min="0"
                          value={cadenceFormData.days_after_enrollment}
                          onChange={(e) => setCadenceFormData({
                            ...cadenceFormData,
                            days_after_enrollment: e.target.value
                          })}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>SMS Template <Tooltip text="Select the SMS message template to send at this step" /></label>
                        <select
                          value={cadenceFormData.sms_template_id}
                          onChange={(e) => setCadenceFormData({
                            ...cadenceFormData,
                            sms_template_id: e.target.value
                          })}
                          style={styles.input}
                        >
                          <option value="">-- None --</option>
                          {smsTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.template_name}</option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email Template <Tooltip text="Select the email template to send at this step" /></label>
                        <select
                          value={cadenceFormData.email_template_id}
                          onChange={(e) => setCadenceFormData({
                            ...cadenceFormData,
                            email_template_id: e.target.value
                          })}
                          style={styles.input}
                        >
                          <option value="">-- None --</option>
                          {emailTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.template_name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={styles.formActions}>
                      <button className="button" onClick={handleSaveCadence}>
                        {editingCadence ? 'Update Step' : 'Add Step'}
                      </button>
                      <button className="button secondary" onClick={resetCadenceForm}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '16px' }}>
                    <button className="button" onClick={() => setShowCadenceForm(true)}>
                      + Add Step
                    </button>
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowCadenceModal(false);
                  setSelectedCampaignType(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Eligibility Preview</h3>

            {previewLoading ? (
              <p>Loading preview...</p>
            ) : previewData ? (
              <>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{previewData.eligible_count}</strong> records eligible for{' '}
                  <strong>{previewData.campaign_type_name}</strong>
                </p>
                {previewData.records.length > 0 && (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.records.map((record) => (
                        <tr key={record.id}>
                          <td>{record.id}</td>
                          <td>{record.first_name} {record.last_name}</td>
                          <td>{record.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {previewData.eligible_count > 10 && (
                  <p style={{ color: '#666', fontSize: '14px' }}>
                    Showing first 10 of {previewData.eligible_count} records.
                  </p>
                )}
              </>
            ) : (
              <p>No data available.</p>
            )}

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowPreview(false);
                  setPreviewData(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Settings Modal */}
      {showVoiceModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Voice AI Settings - {selectedCampaignType?.name}</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Configure voice AI calling for this campaign type.
            </p>

            {voiceLoading ? (
              <p>Loading...</p>
            ) : voiceConfig ? (
              <div style={styles.voiceForm}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={voiceConfig.voice_enabled === 1}
                      onChange={(e) => setVoiceConfig({
                        ...voiceConfig,
                        voice_enabled: e.target.checked ? 1 : 0
                      })}
                      style={{ marginRight: '8px' }}
                    />
                    Enable Voice AI Calls
                  </label>
                  <p style={styles.helpText}>
                    When enabled, patients will receive automated voice calls as part of this campaign.
                  </p>
                </div>

                {voiceConfig.voice_enabled === 1 && (
                  <>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Voice Priority <Tooltip text="Determines whether voice calls are made before or after SMS/Email" /></label>
                        <select
                          value={voiceConfig.voice_channel_priority}
                          onChange={(e) => setVoiceConfig({
                            ...voiceConfig,
                            voice_channel_priority: parseInt(e.target.value)
                          })}
                          style={styles.input}
                        >
                          <option value={1}>Voice First (call before SMS/Email)</option>
                          <option value={2}>Voice as Fallback (call after SMS/Email)</option>
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Max Call Attempts <Tooltip text="Maximum number of call retries if patient doesn't answer (1-5)" /></label>
                        <input
                          type="number"
                          min={1}
                          max={5}
                          value={voiceConfig.voice_max_attempts}
                          onChange={(e) => setVoiceConfig({
                            ...voiceConfig,
                            voice_max_attempts: parseInt(e.target.value)
                          })}
                          style={styles.input}
                        />
                      </div>
                    </div>

                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Quiet Hours Start <Tooltip text="Time after which no calls will be made (respects patient timezone)" /></label>
                        <input
                          type="time"
                          value={voiceConfig.voice_quiet_hours_start}
                          onChange={(e) => setVoiceConfig({
                            ...voiceConfig,
                            voice_quiet_hours_start: e.target.value
                          })}
                          style={styles.input}
                        />
                        <p style={styles.helpText}>No calls after this time</p>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Quiet Hours End <Tooltip text="Time after which calls can resume in the morning" /></label>
                        <input
                          type="time"
                          value={voiceConfig.voice_quiet_hours_end}
                          onChange={(e) => setVoiceConfig({
                            ...voiceConfig,
                            voice_quiet_hours_end: e.target.value
                          })}
                          style={styles.input}
                        />
                        <p style={styles.helpText}>Calls resume after this time</p>
                      </div>
                    </div>

                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Retry Delay (Hours) <Tooltip text="Wait time between retry call attempts (1-168 hours)" /></label>
                        <input
                          type="number"
                          min={1}
                          max={168}
                          value={voiceConfig.voice_retry_delay_hours}
                          onChange={(e) => setVoiceConfig({
                            ...voiceConfig,
                            voice_retry_delay_hours: parseInt(e.target.value)
                          })}
                          style={styles.input}
                        />
                        <p style={styles.helpText}>Hours between retry attempts</p>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          <input
                            type="checkbox"
                            checked={voiceConfig.sms_fallback_enabled === 1}
                            onChange={(e) => setVoiceConfig({
                              ...voiceConfig,
                              sms_fallback_enabled: e.target.checked ? 1 : 0
                            })}
                            style={{ marginRight: '8px' }}
                          />
                          SMS Fallback
                        </label>
                        <p style={styles.helpText}>Send SMS if call fails or goes unanswered</p>
                      </div>
                    </div>
                  </>
                )}

                <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    className="button"
                    onClick={handleSaveVoiceConfig}
                    disabled={voiceSaving}
                  >
                    {voiceSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                  <button
                    className="button secondary"
                    onClick={() => {
                      setShowVoiceModal(false);
                      setSelectedCampaignType(null);
                      setVoiceConfig(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p>Failed to load voice configuration.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  subNav: {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
    borderBottom: '2px solid #e9ecef',
    paddingBottom: '0',
  },
  subNavTab: {
    padding: '12px 20px',
    textDecoration: 'none',
    color: '#6c757d',
    fontWeight: '500',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s ease',
  },
  subNavTabActive: {
    color: '#0d6efd',
    borderBottomColor: '#0d6efd',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
  },
  formContainer: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  configSection: {
    background: '#fff',
    padding: '16px',
    borderRadius: '4px',
    marginBottom: '16px',
    border: '1px solid #e0e0e0',
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
    marginTop: '8px',
  },
  configItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  configLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '4px',
  },
  ruleBuilderHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  rulesContainer: {
    marginTop: '12px',
  },
  ruleRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '8px',
    padding: '8px',
    background: '#f8f9fa',
    borderRadius: '4px',
  },
  formActions: {
    display: 'flex',
    gap: '10px',
  },
  cadenceForm: {
    background: '#f8f9fa',
    padding: '16px',
    borderRadius: '8px',
    marginTop: '16px',
    border: '1px solid #e0e0e0',
  },
  cadenceFormRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  actionButton: {
    padding: '4px 10px',
    fontSize: '12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    background: '#667eea',
    color: '#fff',
  },
  dangerButton: {
    background: '#dc3545',
  },
  successButton: {
    background: '#28a745',
  },
  warningButton: {
    background: '#ffc107',
    color: '#333',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    padding: '24px',
    borderRadius: '8px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  voiceForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  helpText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
};

export default CampaignTypes;
